import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { RktButtonDirective } from '@rocketfinancialcorp/rocket-ui/button';
import { RktFormComponent } from '@rocketfinancialcorp/rocket-ui/form';

import { LinxAccountType, LinxEnhanceAccountType, LinxEntityType } from '@shared/constants';
import {
  CustomerVerificationStatus,
  FormModel,
  LinxEnhanceAccountDetails,
  LinxEnhanceProfileDetails,
  LinxOtherDocument,
} from '@shared/models';
import { emptySpacesValidator } from '@shared/validators';

import { idDocumentBusinessFields, idDocumentIndividualFields, linxRequestProfileFields } from './form-fields';

interface EnhanceProfileFormModel {
  businessInfo: Record<string, boolean>;
  personalInfo: Record<string, boolean>;
  addresses: Record<string, boolean>;
  phoneNumber: string;
  documentTypes: Record<string, boolean>;
}

const OTHER_DOCUMENT_TYPE = 'other';

type OtherDocumentForm = FormGroup<{ name: FormControl<string>; description: FormControl<string> }>;

/** Names are the natural key for "other" documents — they must be unique per config (case-insensitive, trimmed). */
const uniqueOtherDocumentNames = (control: AbstractControl): ValidationErrors | null => {
  const names = (control as FormArray).controls
    .map((group) => (group.get('name')?.value ?? '').trim().toLowerCase())
    .filter((name: string) => name.length);
  return new Set(names).size === names.length ? null : { duplicateName: true };
};

@Component({
  selector: 'app-enhance-profile-edit',
  templateUrl: './enhance-profile-edit.component.html',
  imports: [RktButtonDirective, FormsModule, ReactiveFormsModule, RktFormComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EnhanceProfileEditComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  entityType = input<LinxEntityType>();

  accountType = input<LinxAccountType | undefined>(LinxAccountType.INDIVIDUAL);

  verificationStatus = input<CustomerVerificationStatus>();

  selectedEnhanceProfileDetails = input<LinxEnhanceAccountDetails>();

  continue = output<LinxEnhanceAccountDetails>();

  profileForm = new FormGroup({});

  formModel: FormModel<EnhanceProfileFormModel> = {};

  otherDocumentsForm = new FormGroup({
    otherDocuments: new FormArray<OtherDocumentForm>([], uniqueOtherDocumentNames),
  });

  isOtherSelected = signal<boolean>(false);

  profileFields = computed(() => {
    return linxRequestProfileFields(this.accountType(), this.entityType(), this.verificationStatus());
  });

  documentFields = computed(() => {
    return this.accountType() === LinxAccountType.BUSINESS ? idDocumentBusinessFields : idDocumentIndividualFields;
  });

  get otherDocuments(): FormArray<OtherDocumentForm> {
    return this.otherDocumentsForm.controls.otherDocuments;
  }

  ngOnInit(): void {
    this.setEnhanceProfile();

    this.profileForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.syncOtherDocumentsVisibility();
    });
  }

  private syncOtherDocumentsVisibility(): void {
    const selected = !!this.formModel.documentTypes?.[OTHER_DOCUMENT_TYPE];
    if (selected === this.isOtherSelected()) {
      return;
    }
    this.isOtherSelected.set(selected);
    if (selected) {
      if (!this.otherDocuments.length) {
        this.addOtherDocument();
      }
    } else {
      this.otherDocuments.clear();
    }
  }

  private buildOtherDocumentGroup(document?: LinxOtherDocument): OtherDocumentForm {
    return new FormGroup({
      name: new FormControl(document?.name ?? '', { nonNullable: true, validators: [Validators.required, emptySpacesValidator] }),
      description: new FormControl(document?.description ?? '', { nonNullable: true }),
    });
  }

  addOtherDocument(document?: LinxOtherDocument): void {
    this.otherDocuments.push(this.buildOtherDocumentGroup(document));
  }

  removeOtherDocument(index: number): void {
    this.otherDocuments.removeAt(index);
    if (!this.otherDocuments.length) {
      this.addOtherDocument();
    }
  }

  private setEnhanceProfile(): void {
    if (!this.selectedEnhanceProfileDetails) return;

    this.formModel = {};
    const { phoneNumber, personalInfo, businessInfo, addresses } = this.selectedEnhanceProfileDetails()?.enhanceProfileDetails ?? {};
    if (phoneNumber) {
      this.formModel.phoneNumber = phoneNumber[0];
    }
    const profileFields: string[] = personalInfo ?? businessInfo ?? [];
    if (profileFields.length) {
      const profileInfo = {} as Record<string, boolean>;
      profileFields.forEach((info: string) => {
        profileInfo[info] = true;
      });
      if (personalInfo) {
        this.formModel.personalInfo = profileInfo;
      } else {
        this.formModel.businessInfo = profileInfo;
      }
    }

    if (addresses) {
      const formAddresses = {} as Record<string, boolean>;
      addresses.forEach((address: string) => {
        formAddresses[address] = true;
      });
      this.formModel.addresses = formAddresses;
    }

    const { documentTypes, otherDocuments } = this.selectedEnhanceProfileDetails() ?? {};
    if (documentTypes?.length) {
      const formDocumentTypes = {} as Record<string, boolean>;
      documentTypes.forEach((type: string) => {
        formDocumentTypes[type] = true;
      });
      this.formModel.documentTypes = formDocumentTypes;
    }

    this.otherDocuments.clear();
    if (documentTypes?.includes(OTHER_DOCUMENT_TYPE)) {
      this.isOtherSelected.set(true);
      if (otherDocuments?.length) {
        otherDocuments.forEach((document) => this.addOtherDocument(document));
      } else {
        this.addOtherDocument();
      }
    }
  }

  onClickContinue(): void {
    const { documentTypes = {}, personalInfo, businessInfo, addresses, phoneNumber } = this.formModel;
    const profileDetails: LinxEnhanceProfileDetails = {};

    if (phoneNumber) {
      profileDetails.phoneNumber = [phoneNumber];
    }

    const personalInfoDetails = personalInfo ? Object.keys(personalInfo).filter((key) => personalInfo[key]) : [];
    if (personalInfoDetails.length) {
      const individualInfo: string[] = [];
      personalInfoDetails.forEach((item: string) => {
        if (item === 'email') {
          profileDetails.email = ['PRIMARY'];
        } else {
          individualInfo.push(item);
        }
      });
      if (individualInfo.length) {
        profileDetails.personalInfo = individualInfo;
      }
    }

    const businessDetails = businessInfo ? Object.keys(businessInfo).filter((key) => businessInfo[key]) : [];
    if (businessDetails.length) {
      const businessProfileInfo: string[] = [];
      businessDetails.forEach((item: string) => {
        if (item === 'contacts') {
          profileDetails.contacts = ['PRIMARY'];
        } else if (item === 'phoneNumber') {
          profileDetails.phoneNumber = ['WORK'];
        } else {
          businessProfileInfo.push(item);
        }
      });
      if (businessProfileInfo.length) {
        profileDetails.businessInfo = businessProfileInfo;
      }
    }

    const addressesDetails = addresses ? Object.keys(addresses).filter((key) => addresses[key]) : [];
    if (addressesDetails.length) {
      profileDetails.addresses = addressesDetails;
    }

    const selectedDocumentTypes = Object.keys(documentTypes).filter((key) => documentTypes[key]);

    const enhanceDetails: LinxEnhanceAccountDetails = {
      accountType: this.getEnhanceAccountType(),
      enhanceProfileDetails: Object.keys(profileDetails).length ? profileDetails : undefined,
      documentTypes: selectedDocumentTypes,
    };

    if (selectedDocumentTypes.includes(OTHER_DOCUMENT_TYPE)) {
      enhanceDetails.otherDocuments = this.buildOtherDocuments();
    }

    this.continue.emit(enhanceDetails);
  }

  private buildOtherDocuments(): LinxOtherDocument[] {
    return this.otherDocuments.controls
      .map(({ value }) => {
        const name = value.name?.trim() ?? '';
        const description = value.description?.trim();
        return description ? { name, description } : { name };
      })
      .filter((document) => document.name.length);
  }

  getEnhanceAccountType(): LinxEnhanceAccountType {
    if (this.entityType() === LinxEntityType.COUNTERPARTY) {
      return LinxEnhanceAccountType.Counterparty;
    }
    return this.accountType() === LinxAccountType.BUSINESS ? LinxEnhanceAccountType.Business : LinxEnhanceAccountType.Individual;
  }

  onClearBtnClick(): void {
    this.profileForm.reset();
    this.otherDocuments.clear();
    this.isOtherSelected.set(false);
  }
}
